import React, { BaseSyntheticEvent, ChangeEvent, ReactNode, useCallback } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useAppDispatch } from '../../../shared/lib/hooks/useAppDispatch/useAppDispatch'
import { useAppSelector } from '../../../shared/lib/hooks/useAppSelector/useAppSelector'
import { modalActions } from '../../../entities/Modal'
import { ModalForm } from '../../../shared/ui/ModalForm/ModalForm'
import {
	clientLabelActions,
	LabelColorPicker,
	selectClientLabelEditable,
	selectIsLoading
} from '../../../entities/ClientLabel'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import { editClientLabel } from '../model/services/editClientLabel'
import { Loader } from '../../../shared/ui/Loader/Loader'
import { selectClientLabels } from '../../DisplayClientLabels'

const style = {
	width: 400,
	p: '2rem 2rem',

	gap: 2
}

const inputStyles = {
	width: '100%'
}

export const EditClientLabelModal = () => {
	const dispatch = useAppDispatch()
	const {
		formState: { errors, isValid },
		register,
		handleSubmit,
		reset
	} = useForm({ mode: 'onBlur' })

	const isLoading = useAppSelector(selectIsLoading)
	const clientLabel = useAppSelector(selectClientLabelEditable)
	const { labelName, labelColor } = clientLabel
	const clientLabels = useAppSelector(selectClientLabels)

	const currentLabel = clientLabels.find(
		label => label.labelId === clientLabel.labelId
	)

	const handleLabelNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		e.preventDefault()
		dispatch(clientLabelActions.setLabelName(e.target.value))
	}, [dispatch])

	const handleLabelColorChange = useCallback((value: string) => {
		dispatch(clientLabelActions.setLabelColor(value))
	}, [dispatch])

	const handleCancel = () => {
		dispatch(modalActions.hideModal())
		dispatch(clientLabelActions.resetClientLabelEditable())
		reset?.()
	}

	const onSubmit: SubmitHandler<{}> = async (_, event) => {
		event?.preventDefault()
		dispatch(clientLabelActions.setIsLoading(true))
		const result = await dispatch(editClientLabel(clientLabel))
		if (result.meta.requestStatus === 'fulfilled') {
			dispatch(modalActions.hideModal())
			dispatch(clientLabelActions.resetClientLabelEditable())
			dispatch(clientLabelActions.setIsLoading(false))
			reset()
		}
		if (result.meta.requestStatus === 'rejected') {
			dispatch(clientLabelActions.setIsLoading(false))
		}
	}

	if (isLoading) return <Loader />

	return (
		<ModalForm
			handleCancel={handleCancel}
			disabled={!isValid}
			handleOnSubmit={(e: BaseSyntheticEvent) =>
				handleSubmit(onSubmit({}, e))
			}
			modalTitle={'Edit label'}
			styles={style}
			disableCloseOnBackdropClick
		>
			<Stack
				direction="column"
				spacing={3}
			>
				<TextField
					required
					type="text"
					sx={inputStyles}
					variant="outlined"
					label="Label name"
					error={!!errors?.labelName?.message}
					helperText={(errors?.labelName?.message as ReactNode) || ''}
					{...register('labelName', {
						required: 'Field is required',
						validate: (name) => {
							const isUnique = name === currentLabel?.labelName ||
								!clientLabels.some((label) => label.labelName === name)

							return isUnique || 'Label with this name already exists'
						}
					})}
					onChange={handleLabelNameChange}
					value={labelName}
				/>
				<LabelColorPicker
					register={register}
					errors={errors}
					labelName={labelName}
					labelColor={labelColor}
					onColorChange={handleLabelColorChange}
					labelId={'editLabelColor'}
					styles={inputStyles}
				/>
			</Stack>
		</ModalForm>
	)
}
